// @flow

import { GridUtils, SpinnerMask } from '@atlassian/amkt-common';
import { getSelectedAppVersion, useAppListingById } from '@atlassian/mpac-app-listing';
import { HostingTypeToQueryParam } from '@atlassian/mpac-types';
import {
  MarketplaceEntityStatusValues,
  MarketplaceLocationValues,
} from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { ConstrainedContainer, useHistory } from '@atlassian/mpac-ui';
import { archivedAppLink, linkTemplates, log, UrlUtils } from '@atlassian/mpac-utils';
import styled from '@emotion/styled';
import capitalize from 'lodash/capitalize';
import React, { useEffect } from 'react';

import NotFound from '../../../../../../../../common/components/NotFound';
import ServerError from '../../../../../../../../common/components/ServerError';
import {
  safeToForwardToPartner,
  trackTabChange,
} from '../../../../../../../../common/utils/analytics/analytics';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import { PAGE_CONTAINER_MAX_WIDTH } from '../../../../../../../constants';
import { AppListingPageTask } from '../../../../../../../telemetry/tasks';
import { APP_LISTING_HOME_LINK } from '../../../constants';
import { getFirstValidHostingType, toAnalyticsHosting } from '../utils';
import AppListingPageContent from './AppListingPageContent';

import type { AppListingTabsType } from '@atlassian/mpac-app-listing';
import type { AtlassianProductHostingType } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';

const { updateQueryParam } = UrlUtils;

export const makeLinkTemplates = () => {
  const {
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    macBaseUrl,
    wacBaseUrl,
  } = BootstrapConfig.get();

  return linkTemplates({
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    macBaseUrl,
    wacBaseUrl,
  });
};

const redirectToArchivedAppPage = appId => {
  if (window.onCaptureReady) {
    window.onCaptureReady(302);
  }

  window.location.href = archivedAppLink().expand({ appId });
};

type AppListingPageProps = {|
  appId: string,
  selectedHosting: ?AtlassianProductHostingType,
  tab: AppListingTabsType,
  showOnlyPublic: boolean,
|};

const Container = styled(ConstrainedContainer)`
  padding: 30px 0;

  @media screen and (max-width: ${PAGE_CONTAINER_MAX_WIDTH}) {
    padding: ${GridUtils.GutterSpacing.Base};
  }

  min-height: 100vh;
  position: relative;
`;

const AppListingPage = (props: AppListingPageProps) => {
  const { appId, selectedHosting, showOnlyPublic } = props;
  const history = useHistory();
  const links = makeLinkTemplates();

  const setNewHostingType = (newHosting: AtlassianProductHostingType) => {
    const updatedPathname = updateQueryParam(
      window.location.href,
      'hosting',
      HostingTypeToQueryParam[newHosting]
    );

    history.replace(updatedPathname);
    safeToForwardToPartner(appId, () => {
      trackTabChange(updatedPathname, toAnalyticsHosting(newHosting), true);
    });
  };

  const setNewPageTab = (tab: AppListingTabsType) => {
    const newPath = updateQueryParam(window.location.href, 'tab', tab);
    history.replace(newPath);
    safeToForwardToPartner(appId, () => {
      trackTabChange(newPath, capitalize(tab), true);
    });
  };

  const redirectToLogin = () => {
    log.info('AppListingPage', 'Auth failure while fetching app listing, redirecting to login');
    window.location.href = `/login?redirect=${encodeURIComponent(window.location.href)}`;
  };

  // run this effect before any other hooks
  useEffect(() => {
    AppListingPageTask.start();
  }, []);

  const { data, error, loading } = useAppListingById({
    appId,
    location: MarketplaceLocationValues.Website,
    showOnlyPublic,
  });

  if (error) {
    /**
     * When a logged-in users' session expires, the gateway treats it as failed-auth intead of
     * downgrading it to an unauthenticated call. Temporarily, we will redirect 401 responses
     * responses to the login page, for the app listing page.
     * See https://ecosystem.atlassian.net/browse/MC-1770
     */
    if (error.networkError?.statusCode === 401) {
      AppListingPageTask.abort();
      redirectToLogin();
      return null;
    }
    return <ServerError homeLink={APP_LISTING_HOME_LINK} />;
  }

  const resolveAppListingContent = () => {
    const notFound = <NotFound homeLink={APP_LISTING_HOME_LINK} />;
    if (!data) {
      if (loading) {
        return null;
      } else {
        return notFound;
      }
    }

    if (data.entityStatus === MarketplaceEntityStatusValues.Archived) {
      return redirectToArchivedAppPage(data.appId);
    }

    const redirectToVersionHistoryPage = () => {
      window.location.href = links.appVersionHistory.expand({
        appId: props.appId,
        appSlug: data.slug,
      });
    };

    const firstValidHosting = selectedHosting || getFirstValidHostingType(data);
    if (firstValidHosting !== selectedHosting) {
      setNewHostingType(firstValidHosting);
    }

    const selectedAppVersion = getSelectedAppVersion(firstValidHosting, data);
    if (!selectedAppVersion) {
      if (data.allServerDCAppVersions.totalCount > 0) {
        redirectToVersionHistoryPage();
        return null;
      }

      return notFound;
    }

    return (
      <AppListingPageContent
        appDetails={data}
        appId={props.appId}
        tab={props.tab}
        links={links}
        selectedHosting={firstValidHosting}
        selectedAppVersion={selectedAppVersion}
        setNewHostingType={setNewHostingType}
        setNewTab={setNewPageTab}
        showOnlyPublic={showOnlyPublic}
      />
    );
  };

  // Show a spinner only when we don't have data
  // from the graphql cache available immediately
  const showSpinner = !data && loading;

  return (
    <SpinnerMask active={showSpinner} delay={1000} size={50}>
      <Container>{resolveAppListingContent()}</Container>
    </SpinnerMask>
  );
};

export default AppListingPage;
